import { AddressType, type Home } from '@lmc/ts-common'
import {
  Add,
  Button,
  Chevron,
  FormCard,
  FormItem,
  Input,
  Select
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Alert, Form } from 'antd'
import { getNominative } from 'common/utils'
import useFeatureFlag from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useRequiredRule from 'screens/Residents/screens/ResidentImportantPeopleScreens/forms/utils/useRequiredRule'
import DeleteAddressModal from 'screens/Rostering/ChangeManagement/DeleteAddressModal'
import useCheckAddress from 'screens/Rostering/hooks/useCheckAddress'
import { AddressForm } from './AddressForm'
import { AddressFormAddressLastUpdated } from './AddressFormAddressLastUpdated'
import { AddressFormMainPermanentResidentFields } from './AddressFormMainPermanentResidentFields'

function ClearMainPermanentResidenceFieldsOnTypeChange({
  fieldIndex
}: {
  fieldIndex: number
}) {
  const form = Form.useFormInstance()
  const type = Form.useWatch(['addresses', fieldIndex, 'type'])
  const prevTypeRef = useRef<AddressType | undefined>(undefined)

  useEffect(() => {
    const prev = prevTypeRef.current
    if (
      prev === AddressType.Main_Permanent_Residence &&
      type !== undefined &&
      type !== AddressType.Main_Permanent_Residence
    ) {
      form.setFieldValue(
        ['addresses', fieldIndex, 'accommodationDetails'],
        undefined
      )
      form.setFieldValue(
        ['addresses', fieldIndex, 'dateOfObservation'],
        undefined
      )
    }
    prevTypeRef.current = type
  }, [type, fieldIndex, form])

  return null
}

type AddressFormCardProps = {
  residentId: string
}

export const AddressFormCard = ({ residentId }: AddressFormCardProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const typeOptions = getTypeOptions(t)
  const requiredRule = useRequiredRule()
  const { hasRota } = useHomeType()
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Service user'
  )
  const addresses = Form.useWatch('addresses') as
    | Array<{ type?: AddressType | null } | null>
    | undefined

  const [showDeleteAddressModal, setShowDeleteAddressModal] = useState(false)

  const flags = useFeatureFlag({
    flagNames: ['rostering_change_management_part_2']
  })?.data

  const flagsRosteringChangeManagementPart2Enabled =
    flags?.rostering_change_management_part_2?.enabled

  const { checkAddressUsedInRota } = useCheckAddress()
  const mainPermanentResidenceCount =
    addresses?.filter(
      (address) => address?.type === AddressType.Main_Permanent_Residence
    ).length ?? 0
  const showMainPermanentResidenceWarning = mainPermanentResidenceCount > 1

  return (
    <Form.List name="addresses">
      {(fields, operations) => (
        <FormCard
          title={t(supConstants.ADDRESS)}
          rightChildren={
            <Button
              type="button"
              variant="text"
              icon={<Add />}
              onClick={() => operations.add()}
            >
              {t(supConstants.ADD_ADDRESS)}
            </Button>
          }
        >
          {showMainPermanentResidenceWarning && (
            <Alert
              type="warning"
              showIcon
              message={t(
                supConstants.MULTIPLE_MAIN_PERMANENT_RESIDENCE_WARNING,
                {
                  residentNominative
                }
              )}
            />
          )}
          {fields.map((field) => (
            <Form.Item
              key={`addresses-${field.name}`}
              noStyle
              dependencies={[['addresses', field.name, 'type']]}
            >
              {({ getFieldValue, setFieldValue }) => {
                const addressType = getFieldValue([
                  'addresses',
                  field.name,
                  'type'
                ]) as AddressType
                const title = addressType
                  ? t(supConstants[addressType])
                  : `${t(supConstants.ADDRESS)} ${field.name + 1}`
                const isAddressFilled = !!getFieldValue([
                  'addresses',
                  field.name
                ])
                const addressId = getFieldValue([
                  'addresses',
                  field.name,
                  '_id'
                ])

                const handleDelete = async () => {
                  if (hasRota && flagsRosteringChangeManagementPart2Enabled) {
                    const addressUsed = addressId
                      ? await checkAddressUsedInRota({
                          addressId,
                          residentId
                        })
                      : {}
                    const areAllArraysEmpty =
                      addressUsed?.instances?.length === 0 &&
                      addressUsed?.patterns?.length === 0 &&
                      addressUsed?.revisions?.length === 0

                    if (areAllArraysEmpty || !addressId)
                      operations.remove(field.name)
                    else setShowDeleteAddressModal(true)
                  } else operations.remove(field.name)
                }

                return (
                  <>
                    <ClearMainPermanentResidenceFieldsOnTypeChange
                      fieldIndex={field.name as number}
                    />
                    <AddressForm
                      onAddressDelete={handleDelete}
                      onAutofill={(values) =>
                        setFieldValue(['addresses', field.name], {
                          ...getFieldValue(['addresses', field.name]),
                          ...values
                        })
                      }
                      fieldName={[field.name]}
                      title={title}
                      key={field.key}
                      initialIsAddingAddressManually={isAddressFilled}
                      afterAddressFields={
                        <>
                          {addressType ===
                          AddressType.Main_Permanent_Residence ? (
                            <AddressFormMainPermanentResidentFields
                              field={field}
                              t={t}
                            />
                          ) : null}
                          <AddressFormAddressLastUpdated field={field} />
                        </>
                      }
                      mandatoryFields={
                        <FormItem
                          {...field}
                          label={<Text>{t(supConstants.ADDRESS_TYPE)}</Text>}
                          name={[field.name, 'type']}
                          rules={[requiredRule(t(supConstants.ADDRESS_TYPE))]}
                        >
                          <Select
                            suffixIcon={<Chevron direction="down" />}
                            options={typeOptions}
                          />
                        </FormItem>
                      }
                    >
                      <FormItem
                        {...field}
                        label={<Text>{t(supConstants.BUILDING_NAME)}</Text>}
                        name={[field.name, 'name']}
                      >
                        <Input />
                      </FormItem>
                      <FormItem
                        {...field}
                        label={<Text>{t(supConstants.ADDRESS_LINE_1)}</Text>}
                        name={[field.name, 'street1']}
                      >
                        <Input />
                      </FormItem>
                      <FormItem
                        {...field}
                        label={<Text>{t(supConstants.ADDRESS_LINE_2)}</Text>}
                        name={[field.name, 'street2']}
                      >
                        <Input />
                      </FormItem>
                      <FormItem
                        {...field}
                        label={<Text>{t(supConstants.TOWN_CITY)}</Text>}
                        name={[field.name, 'suburb']}
                      >
                        <Input />
                      </FormItem>
                      <FormItem
                        {...field}
                        label={<Text>{t(supConstants.COUNTRY)}</Text>}
                        name={[field.name, 'state']}
                      >
                        <Input />
                      </FormItem>
                    </AddressForm>
                  </>
                )
              }}
            </Form.Item>
          ))}
          {showDeleteAddressModal && (
            <DeleteAddressModal
              showModal={showDeleteAddressModal}
              setShowModal={setShowDeleteAddressModal}
            />
          )}
        </FormCard>
      )}
    </Form.List>
  )
}

export const getTypeOptions = (t: (values: string) => string) => {
  const options = [
    {
      value: AddressType['Correspondence_(Non-Residence)'],
      label: (
        <Text>
          {t(supConstants[AddressType['Correspondence_(Non-Residence)']])}
        </Text>
      )
    },
    {
      value: AddressType.Invoice,
      label: <Text>{t(supConstants[AddressType.Invoice])}</Text>
    },
    {
      value: AddressType.Main_Business_Premises,
      label: <Text>{t(supConstants[AddressType.Main_Business_Premises])}</Text>
    },
    {
      value: AddressType.Main_Permanent_Residence,
      label: (
        <Text>{t(supConstants[AddressType.Main_Permanent_Residence])}</Text>
      )
    },
    {
      value: AddressType.Other_Business_Premises,
      label: <Text>{t(supConstants[AddressType.Other_Business_Premises])}</Text>
    },
    {
      value: AddressType.Other_Permanent_Residence,
      label: (
        <Text>{t(supConstants[AddressType.Other_Permanent_Residence])}</Text>
      )
    },
    {
      value: AddressType.Safe_Haven_ADDRESS,
      label: <Text>{t(supConstants[AddressType.Safe_Haven_ADDRESS])}</Text>
    },
    {
      value: AddressType.Temporary_Residence,
      label: <Text>{t(supConstants[AddressType.Temporary_Residence])}</Text>
    }
  ]

  return options
}
